import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TableHeader } from 'storybook';

export const useTableHeaders = () => {
   const { t } = useTranslation();

   const clientOrdersTableHeaders: {
      label: string;
      field: string;
      sortable?: boolean;
      align?: 'left' | 'right' | 'center';
   }[] = useMemo(
      () => [
         { field: 'orderId', label: t('client_orders.table.id') },
         { field: 'internalOrderId', label: t('client_orders.table.internal_order_id') },
         { field: 'orderDate', label: t('client_orders.table.order_date') },
         { field: 'deliveredDate', label: t('client_orders.table.delivered_date') },
         { field: 'sendPalletsDate', label: t('client_orders.table.send_pallets_date') },
         { field: 'status', label: t('client_orders.table.status') },
         { field: 'palletsAmount', label: t('client_orders.table.pallets_amount'), align: 'right' },
         { field: 'actions', label: t('client_orders.table.actions') },
      ],
      [t],
   );

   const clientOrdersTableHeadersWithTags: {
      label: string;
      field: string;
      sortable?: boolean;
      align?: 'left' | 'right' | 'center';
   }[] = useMemo(
      () => [{ field: 'tags', label: '' }, ...clientOrdersTableHeaders],
      [clientOrdersTableHeaders],
   );

   const supplierOrdersTableHeaders: {
      label: string;
      field: string;
      sortable?: boolean;
      align?: 'left' | 'right' | 'center';
   }[] = useMemo(
      () => [
         { field: 'orderId', label: t('supplier_orders.table.id') },
         { field: 'clientName', label: t('supplier_orders.table.client_name') },
         { field: 'orderDate', label: t('client_orders.table.order_date') },
         { field: 'deliveredDate', label: t('supplier_orders.table.delivered_date') },
         { field: 'shipmentDate', label: t('client_orders.table.send_pallets_date') },
         { field: 'status', label: t('client_orders.table.status') },
         {
            field: 'palletsAmount',
            label: t('client_orders.table.pallets_amount'),
            align: 'right',
         },
         { field: 'actions', label: t('client_orders.table.actions') },
      ],
      [t],
   );

   const distributionCenterOrdersTableHeaders: {
      label: string;
      field: string;
      sortable?: boolean;
      align?: 'left' | 'right' | 'center';
   }[] = useMemo(
      () => [
         {
            field: 'externalCommercialNetworkNumber',
            label: t('distribution_center_orders.table.external_commercial_network_number'),
         },
         { field: 'orderId', label: t('supplier_orders.table.id') },
         { field: 'clientName', label: t('supplier_orders.table.client_name') },
         { field: 'shipmentDate', label: t('client_orders.table.send_pallets_date') },
         { field: 'palletsAmount', label: t('client_orders.table.pallets_amount') },
         { field: '', label: '' },
         { field: 'actions', label: t('client_orders.table.actions') },
      ],
      [t],
   );

   const supplierOrdersTableHeadersWithTags: {
      label: string;
      field: string;
      sortable?: boolean;
      align?: 'left' | 'right' | 'center';
   }[] = useMemo(
      () => [{ field: 'tags', label: '' }, ...supplierOrdersTableHeaders],
      [supplierOrdersTableHeaders],
   );

   const lostDamagedPalletsByClientTableHeaders = useMemo(
      () => [
         { field: 'palletType', label: t('damaged_by_client.table.pallet_type') },
         { field: 'damageType', label: t('damaged_by_client.table.damage_type') },
         { field: 'palletsAmount', label: t('client_orders.table.pallets_amount') },
         { field: 'removeItemBtn', label: '' },
      ],
      [t],
   );

   const lostDamagedPalletsBySupplierTableHeaders = useMemo(
      () => [
         { field: 'palletType', label: t('damaged_by_client.table.pallet_type') },
         { field: 'damageType', label: t('damaged_by_client.table.damage_type') },
         { field: 'palletsAmount', label: t('client_orders.table.pallets_amount') },
         { field: 'images', label: t('damaged_by_supplier.table.images') },
         { field: 'removeItemBtn', label: '' },
      ],
      [t],
   );

   const sendDamagedPalletsTableHeaders = useMemo(
      () => [
         { field: 'warningIcon', label: '' },
         { field: 'sendDate', label: t('send_damaged_pallets.table.send_date') },
         {
            field: 'externalCommercialNetworkNumber',
            label: t('send_damaged_pallets.table.external_order_number'),
            additionalInfo: '(opcj.)',
            noWrapText: true,
         },
         { field: 'commercialNetworkName', label: t('send_damaged_pallets.table.network_name') },
         {
            field: 'commercialNetworkAddress',
            label: t('send_damaged_pallets.table.network_address'),
         },
         { field: 'palletType', label: t('damaged_by_client.table.pallet_type') },
         { field: 'palletsAmount', label: t('client_orders.table.pallets_amount') },
         { field: 'removeItemBtn', label: '' },
      ],
      [t],
   );

   const sendPalletsTableHeaders = useMemo(
      () => [
         { field: 'sendDate', label: t('send_damaged_pallets.table.send_date') },
         {
            field: 'externalCommercialNetworkNumber',
            label: t('send_damaged_pallets.table.external_order_number'),
            additionalInfo: '(opcj.)',
            noWrapText: true,
         },
         { field: 'commercialNetworkName', label: t('send_damaged_pallets.table.network_name') },
         { field: 'commercialNetworkAddress', label: t('send_damaged_pallets.table.network_address') },
         { field: 'palletType', label: t('damaged_by_client.table.pallet_type') },
         { field: 'palletsAmount', label: t('client_orders.table.pallets_amount') },
         { field: 'removeItemBtn', label: '' },
      ],
      [t],
   );

   const editPalletsTableHeaders = useMemo(
      () => [
         { field: 'damaged', label: t('edit_pallets.table.damaged') },
         { field: 'sendDate', label: t('send_damaged_pallets.table.send_date') },
         {
            field: 'externalCommercialNetworkNumber',
            label: t('send_damaged_pallets.table.external_order_number'),
            additionalInfo: '(opcj.)',
            noWrapText: true,
         },
         { field: 'commercialNetworkName', label: t('send_damaged_pallets.table.network_name') },
         { field: 'commercialNetworkAddress', label: t('send_damaged_pallets.table.network_address') },
         { field: 'palletType', label: t('damaged_by_client.table.pallet_type') },
         { field: 'palletsAmount', label: t('client_orders.table.pallets_amount') },
         { field: 'removeItemBtn', label: '' },
      ],
      [t],
   );

   const damagedDestroyedLostPalletsByClientTableHeaders: TableHeader[] = useMemo(
      () => [
         { field: 'orderId', label: t('damaged_lost_by_client.table.order_id') },
         { field: 'damageType', label: t('damaged_by_client.table.damage_type') },
         { field: 'palletType', label: t('damaged_by_client.table.pallet_type') },
         {
            field: 'palletsAmount',
            label: t('client_orders.table.pallets_amount'),
            align: 'right',
         },
      ],
      [t],
   );

   const lostDamagedPalletsInDeliveryClientTableHeaders: TableHeader[] = useMemo(
      () => [
         { field: 'orderId', label: t('in_delivery.table.order_id') },
         { field: 'reportStatus', label: t('in_delivery.table.report_status') },
         { field: 'damageType', label: t('damaged_by_client.table.damage_type') },
         { field: 'palletType', label: t('damaged_by_client.table.pallet_type') },
         { field: 'palletsAmount', label: t('client_orders.table.pallets_amount'), align: 'right' },
         { field: 'images', label: t('damaged_by_supplier.table.images'), align: 'right' },
      ],
      [t],
   );

   const lostDamagedPalletsSupplierOrderDetailsTableHeaders: TableHeader[] = useMemo(
      () => [
         { field: 'reportStatus', label: t('in_delivery.table.report_status') },
         { field: 'palletType', label: t('damaged_by_client.table.pallet_type') },
         {
            field: 'palletsAmount',
            label: t('client_orders.table.pallets_amount'),
            align: 'right',
         },
         { field: 'images', label: t('damaged_by_supplier.table.images'), align: 'right' },
      ],
      [t],
   );

   const lostDamagedPalletsSupplierReportDetailsTableHeaders: TableHeader[] = useMemo(
      () => [
         { field: 'palletType', label: t('damaged_by_client.table.pallet_type') },
         {
            field: 'palletsAmount',
            label: t('client_orders.table.pallets_amount'),
            align: 'right',
         },
         { field: 'images', label: t('damaged_by_supplier.table.images'), align: 'right' },
      ],
      [t],
   );

   const clientsTableHeaders = useMemo(
      () => [
         { field: 'blocked', label: '' },
         { field: 'name', label: t('supplier_orders.table.client_name') },
         { field: 'id', label: t('clients.table.id') },
         { field: 'nip', label: t('clients.table.nip') },
         { field: 'contactEmail', label: t('clients.table.email') },
         { field: 'phoneNumber', label: t('sent_password_reset.phone') },
         { field: 'actions', label: t('client_orders.table.actions') },
      ],
      [t],
   );

   const additionalCostsClientTableHeaders: TableHeader[] = useMemo(
      () => [
         { field: 'name', label: t('damaged_by_client.table.pallet_type') },
         { field: 'type', label: t('additional_costs_client.table.charge_type') },
         { field: 'period', label: t('additional_costs_client.table.period') },
         {
            field: 'palletsAmount',
            label: t('additional_costs_client.table.pallets_amount'),
            align: 'right',
         },
      ],
      [t],
   );

   const additionalCostsSupplierTableHeaders: TableHeader[] = useMemo(
      () => [
         { field: 'name', label: t('damaged_by_client.table.pallet_type') },
         { field: 'type', label: t('additional_costs_client.table.charge_type') },
         { field: 'period', label: t('additional_costs_client.table.period') },
         {
            field: 'palletsAmount',
            label: t('additional_costs_client.table.pallets_amount'),
            align: 'right',
         },
         {
            field: 'chargePrice',
            label: t('additional_costs_supplier.table.charge_price'),
            align: 'right',
         },
         {
            field: 'totalChargesAmount',
            label: t('additional_costs_supplier.table.total'),
            align: 'right',
         },
      ],
      [t],
   );

   const sendPalletsSectionTableHeaders: TableHeader[] = useMemo(
      () => [
         { field: 'shipmentDate', label: t('send_damaged_pallets.table.send_date') },
         { field: 'orderNumber', label: t('send_damaged_pallets.table.external_order_number') },
         { field: 'companyName', label: t('send_damaged_pallets.table.network_name') },
         { field: 'address', label: t('send_damaged_pallets.table.network_address') },
         { field: 'name', label: t('damaged_by_client.table.pallet_type') },
         { field: 'amount', label: t('client_orders.table.pallets_amount'), align: 'right' },
      ],
      [t],
   );

   const sendPalletsSectionTableHeadersWithDamagedPallets: TableHeader[] = useMemo(
      () => [{ field: 'sendDamagedPalletsAlert', label: '' }, ...sendPalletsSectionTableHeaders],
      [sendPalletsSectionTableHeaders],
   );

   const commentsTableHeaders: TableHeader[] = useMemo(
      () => [
         { field: 'date', label: t('comments.table.date') },
         { field: 'content', label: t('comments.table.content') },
         { field: 'actions', label: t('client_orders.table.actions'), align: 'right' },
      ],
      [t],
   );

   const documentsTableHeaders: TableHeader[] = useMemo(
      () => [
         { field: 'name', label: t('documents.table.name') },
         { field: 'createdAt', label: t('documents.table.created_at') },
         { field: 'actions', label: t('client_orders.table.actions'), align: 'right' },
      ],
      [t],
   );

   const documentsTableHeadersWithCanceledInvoices: TableHeader[] = useMemo(
      () => [{ field: 'canceledInvoiceAlert', label: '' }, ...documentsTableHeaders],
      [documentsTableHeaders],
   );

   const commonPalletsPricingTableHeaders = useMemo(
      () => [
         t('pricing.common.pallet_type'),
         t('pricing.common.pickup_price'),
         t('pricing.common.damage'),
         t('pricing.common.lost_or_destroyed'),
         t('pricing.common.sent_to_other'),
      ],
      [t],
   );

   const palletsPricingPerAddressTableHeaders = useMemo(
      () => [t('pricing.common.pallet_type'), t('pricing.address.delivery_price')],
      [t],
   );

   const commercialNetworksTableHeaders = useMemo(
      () => [
         { field: 'commercialNetworkName', label: t('commercial_network.table.name') },
         { field: 'commercialNetworkAddress', label: t('commercial_network.table.address') },
         { field: 'removeRowButton', label: '' },
      ],
      [t],
   );

   const externalStorageTableHeaders: TableHeader[] = useMemo(
      () => [
         { field: 'name', label: t('pricing.common.pallet_type') },
         { field: 'readyToRent', label: t('storage.external.table.ready'), align: 'right' },
         { field: 'damaged', label: t('edit_pallets.table.damaged'), align: 'right' },
      ],
      [t],
   );

   const internalStorageTableHeaders: TableHeader[] = useMemo(
      () => [
         ...externalStorageTableHeaders,
         { field: 'lost', label: t('storage.internal.table.lost'), align: 'right' },
      ],
      [externalStorageTableHeaders, t],
   );

   const supplierPalletsTableHeaders: TableHeader[] = useMemo(
      () => [
         { field: 'tags', label: '' },
         { field: 'orderId', label: t('supplier_pallets.table.order_id') },
         { field: 'clientName', label: t('supplier_orders.table.client_name') },
         { field: 'orderDate', label: t('supplier_pallets.table.order_date') },
         { field: 'deliveredDate', label: t('in_delivery.table.report_status') },
         {
            field: 'shipmentDate',
            label: t('client_orders.table.pallets_amount'),
            align: 'right',
         },
         { field: 'status', label: t('damaged_by_client.table.damage_type') },
         { field: 'actions', label: t('client_orders.table.actions'), align: 'right' },
      ],
      [t],
   );

   const commercialNetworkTableHeaders: TableHeader[] = useMemo(
      () => [
         { field: 'name', label: t('commercial_network.table.name') },
         { field: 'address', label: t('commercial_network.table.address') },
         { field: 'collect all', label: t('commercial_network.table.collect_all'), align: 'right' },
         {
            field: 'collect valid',
            label: t('commercial_network.table.collect_valid'),
            align: 'right',
         },
         {
            field: 'collect invalid',
            label: t('commercial_network.table.collect_invalid'),
            align: 'right',
         },
         { field: 'return all', label: t('commercial_network.table.return_all'), align: 'right' },
         {
            field: 'return valid',
            label: t('commercial_network.table.return_valid'),
            align: 'right',
         },
         {
            field: 'return invalid',
            label: t('commercial_network.table.return_invalid'),
            align: 'right',
         },
         { field: 'actions', label: t('client_orders.table.actions'), align: 'right' },
      ],
      [t],
   );

   const pickUpsTableHeaders: TableHeader[] = useMemo(
      () => [
         { field: 'date', label: t('pickups.table.date') },
         { field: 'hour', label: t('pickups.table.hour') },
         { field: 'pallet types', label: t('pickups.table.pallet_types') },
         { field: 'collect all', label: t('commercial_network.table.collect_all'), align: 'right' },
         { field: 'collect valid', label: t('commercial_network.table.collect_valid'), align: 'right' },
         { field: 'collect invalid', label: t('commercial_network.table.collect_invalid'), align: 'right' },
      ],
      [t],
   );

   const distributionCenterPalletsTableHeaders = useMemo(
      () => [
         { field: 'name', label: t('pricing.common.pallet_type') },
         { field: 'collect all', label: t('commercial_network.table.collect_all') },
         { field: 'collect valid', label: t('commercial_network.table.collect_valid') },
         { field: 'collect invalid', label: t('commercial_network.table.collect_invalid') },
         { field: 'return all', label: t('commercial_network.table.return_all') },
         { field: 'return valid', label: t('commercial_network.table.return_valid') },
         { field: 'return invalid', label: t('commercial_network.table.return_invalid') },
      ],
      [t],
   );

   const panelDistributionCenterPalletsTableHeaders = useMemo(
      () => [
         { field: 'name', label: t('pricing.common.pallet_type') },
         { field: 'balance all', label: t('distribution_center.panel.table.balance_all') },
      ],
      [t],
   );

   const distributionCenterPalletsTableHeadersChangeOrder = useMemo(
      () => [
         { field: 'name', label: t('pricing.common.pallet_type') },
         { field: 'return valid', label: t('commercial_network.table.return_valid') },
         {
            field: 'return invalid',
            label: t('commercial_network.table.return_invalid'),
         },
         { field: 'return all', label: t('commercial_network.table.return_all') },
         {
            field: 'collect valid',
            label: t('commercial_network.table.collect_valid'),
         },
         {
            field: 'collect invalid',
            label: t('commercial_network.table.collect_invalid'),
         },
         { field: 'collect all', label: t('commercial_network.table.collect_all') },
      ],
      [t],
   );

   const distributionCenterPalletsTransferTableHeaders: TableHeader[] = useMemo(
      () => [
         { field: 'name', label: t('pricing.common.pallet_type') },
         {
            field: 'collect valid',
            label: t('commercial_network.table.collect_valid'),
            align: 'right',
         },
         {
            field: 'collect invalid',
            label: t('commercial_network.table.collect_invalid'),
            align: 'right',
         },
         {
            field: 'collect all',
            label: t('commercial_network.table.collect_all'),
            align: 'right',
         },
      ],
      [t],
   );

   const editUserBalanceHeaders: TableHeader[] = useMemo(
      () => [
         { field: 'name', label: t('pricing.common.pallet_type') },
         { field: 'rented', label: t('edit_user_balance.table.rented'), align: 'right' },
         {
            field: 'lost by user fault',
            label: t('edit_user_balance.table.lost_user_fault'),
            align: 'right',
         },
         {
            field: 'damaged by user fault',
            label: t('edit_user_balance.table.damaged_user_fault'),
            align: 'right',
         },
         {
            field: 'received damaged',
            label: t('edit_user_balance.table.received_damaged'),
            align: 'right',
         },
         { field: 'sum', label: t('edit_user_balance.table.sum'), align: 'right' },
      ],
      [t],
   );

   const clientPalletBalanceTableHeaders: TableHeader[] = useMemo(
      () => [
         { field: 'companyName', label: t('supplier_orders.table.client_name') },
         {
            field: 'palletBalance',
            label: t('client_balance.table.pallet_balance'),
            align: 'right',
         },
         {
            field: 'edhpPallet',
            label: t('client_balance.table.edhp'),
            additionalInfo: '800x1200 mm',
            align: 'right',
         },
         {
            field: 'euroPallet',
            label: t('client_balance.table.euro'),
            additionalInfo: '800x1200 mm',
            align: 'right',
         },
         {
            field: 'halfPalletNDHP',
            label: t('client_balance.table.ndhp'),
            additionalInfo: '600x800 mm',
            align: 'right',
         },
         {
            field: 'quarterPalletCDHP',
            label: t('client_balance.table.cdhp'),
            additionalInfo: '400x600 mm',
            align: 'right',
         },
         { field: '', label: t('client_orders.table.actions'), align: 'right' },
      ],
      [t],
   );

   const supplierPalletsPickUpsTableHeadersBase = useMemo(
      () => [
         { field: 'commercialNetworkName', label: t('supplier_pickups.table.network_name') },
         { field: 'createdDate', label: t('supplier_pallets.table.order_date') },
         { field: 'pickUpDate', label: t('pickups.table.date') },
         { field: 'pickUpTime', label: t('supplier_pickups.table.pickup_time') },
         { field: 'status', label: t('supplier_pickups.table.status') },
         { field: 'palletBalances', label: t('supplier_pickups.table.to_collect') },
      ],
      [t],
   );

   const supplierPalletsPickUpsTableHeadersWithActionsAndTags = useMemo(
      () => [
         { field: 'tags', label: '' },
         ...supplierPalletsPickUpsTableHeadersBase,
         { field: 'actions', label: t('client_orders.table.actions') },
      ],
      [supplierPalletsPickUpsTableHeadersBase, t],
   );

   return {
      clientOrdersTableHeaders,
      clientOrdersTableHeadersWithTags,
      supplierOrdersTableHeaders,
      supplierOrdersTableHeadersWithTags,
      distributionCenterPalletsTransferTableHeaders,
      editUserBalanceHeaders,
      clientPalletBalanceTableHeaders,
      supplierPalletsPickUpsTableHeadersBase,
      supplierPalletsPickUpsTableHeadersWithActionsAndTags,
      distributionCenterOrdersTableHeaders,
      lostDamagedPalletsByClientTableHeaders,
      lostDamagedPalletsBySupplierTableHeaders,
      sendDamagedPalletsTableHeaders,
      sendPalletsTableHeaders,
      editPalletsTableHeaders,
      damagedDestroyedLostPalletsByClientTableHeaders,
      lostDamagedPalletsInDeliveryClientTableHeaders,
      lostDamagedPalletsSupplierOrderDetailsTableHeaders,
      lostDamagedPalletsSupplierReportDetailsTableHeaders,
      clientsTableHeaders,
      additionalCostsClientTableHeaders,
      additionalCostsSupplierTableHeaders,
      sendPalletsSectionTableHeaders,
      sendPalletsSectionTableHeadersWithDamagedPallets,
      commentsTableHeaders,
      documentsTableHeadersWithCanceledInvoices,
      commonPalletsPricingTableHeaders,
      palletsPricingPerAddressTableHeaders,
      commercialNetworksTableHeaders,
      internalStorageTableHeaders,
      supplierPalletsTableHeaders,
      commercialNetworkTableHeaders,
      pickUpsTableHeaders,
      distributionCenterPalletsTableHeaders,
      panelDistributionCenterPalletsTableHeaders,
      distributionCenterPalletsTableHeadersChangeOrder,
      externalStorageTableHeaders,
      documentsTableHeaders,
   };
};
